import type { FC } from "react";
import { useMemo } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Alert } from "react-daisyui";

import useTrips from "@/api/trips/useTrips.ts";
import useTripUpdate from "@/api/trips/useTripUpdate.ts";
import usePrepareDestination from "@/api/itineraries/usePrepareDestination.ts";
import TripManipulationForm, {
  type ActiveTripCreateStep,
  type OnCompleteArgs,
} from "@/components/functional/TripManipulationForm/TripManipulationForm.tsx";
import LoaderWrapper from "@/components/ui/LoaderWrapper/LoaderWrapper.tsx";
import { useAuthContext } from "@/context/AuthContext.tsx";
import Routes from "@/router/Routes.ts";
import { passToRoute } from "@/utils/passToRoute.ts";
import shouldPrepareDestination from "@/utils/trip/shouldPrepareDestination.ts";
import TripCreateStep, {
  allTripCreateSteps,
} from "@/_constants/TripCreateStep.ts";

type TripEditNavigationState = {
  tripEdit?: {
    startStep: ActiveTripCreateStep;
    endStep: ActiveTripCreateStep;
    returnTo: string;
  };
};

const TripEditPage: FC = () => {
  const { user } = useAuthContext();
  const { id: tripId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    data: trips,
    isLoading: isLoadingTrips,
    error: errorLoadingTrips,
  } = useTrips(!!tripId, {
    id: tripId,
    user_id: user?.id,
  });
  const { mutateAsync: updateTrip, isPending: isUpdatingTrip } =
    useTripUpdate();
  const { mutateAsync: prepareDestination } = usePrepareDestination();

  const currentTrip = useMemo(() => trips?.[0] ?? null, [trips]);
  const navigationState = location.state as TripEditNavigationState | null;
  const requestedEdit = navigationState?.tripEdit;
  const stateTripEdit =
    requestedEdit &&
    allTripCreateSteps.includes(requestedEdit.startStep) &&
    allTripCreateSteps.includes(requestedEdit.endStep) &&
    requestedEdit.returnTo.startsWith("/")
      ? requestedEdit
      : undefined;

  const queryStep = searchParams.get("step");
  const queryEditStep =
    queryStep &&
    allTripCreateSteps.includes(queryStep as ActiveTripCreateStep)
      ? (queryStep as ActiveTripCreateStep)
      : undefined;

  if (isLoadingTrips) {
    return (
      <div className="fixed inset-0 min-h-screen w-screen bg-journi-theme-light">
        <LoaderWrapper isLoading />
      </div>
    );
  }

  if (!tripId || !currentTrip) {
    return <Navigate to={Routes.DASHBOARD} />;
  }

  const confirmationRoute = passToRoute(Routes.TRIP__CONFIRM, {
    id: tripId,
  });

  const requestedReturnTo = searchParams.get("returnTo");
  const queryReturnTo = requestedReturnTo?.startsWith("/")
    ? requestedReturnTo
    : confirmationRoute;
  const tripEdit = queryEditStep
    ? {
        startStep: queryEditStep,
        endStep: queryEditStep,
        returnTo: queryReturnTo,
      }
    : stateTripEdit;

  const handleCompleteWhenDirty = async ({ step, context }: OnCompleteArgs) => {
    try {
      await updateTrip([{ data: context }, { id: currentTrip.id }]);

      if (shouldPrepareDestination(step, context)) {
        prepareDestination({ trip_id: currentTrip.id }).catch((error: unknown) => {
          console.error("Error preparing the destination:", error);
        });
      }
    } catch (error) {
      console.error("Error updating trip:", error);
      throw new Error((error as Error)?.message ?? "Error updating trip");
    }
  };

  const handleComplete = async ({ step }: OnCompleteArgs) => {
    if (tripEdit) {
      if (step === tripEdit.endStep) {
        navigate(tripEdit.returnTo, {
          replace: true,
          state: null,
        });
      }

      return;
    }

    if (step === TripCreateStep.MORE_INFO) {
      navigate(confirmationRoute, {
        replace: true,
        state: null,
      });
    }
  };

  return (
    <div className="fixed inset-0 min-h-screen w-screen overflow-y-auto bg-journi-theme-light">
      {errorLoadingTrips ? (
        <Alert status="error" className="m-6">
          {errorLoadingTrips.message}
        </Alert>
      ) : null}

      <TripManipulationForm
        initialData={currentTrip.data}
        initialStep={tripEdit?.startStep}
        singleStep={!!tripEdit}
        isLoadingStep={isUpdatingTrip}
        onCompleteWhenDirty={handleCompleteWhenDirty}
        onComplete={handleComplete}
      />
    </div>
  );
};

export default TripEditPage;
